import { useState } from 'react';
import { useStore } from 'zustand';

import { type Entity, type SceneDocument } from '@/domain/model';
import { type EditorStore } from '@/store';

import { PanelSection } from './Inspector';
import { colour } from './theme';

/**
 * Where a point sits along its segment, as a fraction.
 *
 * ## Why a panel and not a right-click menu
 *
 * Because every other construction parameter in this product is edited from the rail — a
 * solid's size, an angle mark's arcs, a graph's formula — and a second way to do the same kind
 * of thing is a second thing to learn. Three more reasons, in order of how much they matter:
 *
 * - **A menu is invisible until you try it.** The rail shows this control the moment a
 *   divisible point is selected, so the capability announces itself; a user who never
 *   right-clicks a point never finds out the product can do this at all.
 * - **A dialog covers the drawing.** Setting a ratio is something you want to *watch*: type
 *   two thirds, see `M` slide up the edge. A modal over the canvas hides the one thing that
 *   tells you whether you typed what you meant.
 * - **It already exists.** `PanelSection`, the selection plumbing and the undo story are here.
 *   A modal is new infrastructure — focus trap, escape, backdrop, and a fight with the canvas
 *   over pointer capture that this project has already had once, over the text editor.
 *
 * None of that argues against a right-click *shortcut* later. It would open this panel; the
 * panel is what has to exist either way.
 *
 * ## Which points qualify
 *
 * Two constructions put a point on a segment, and they arrive by opposite routes. A
 * `locus-point` was **dragged** there and positioned by eye. A `ratio-point` was **stated** —
 * `divide A B 2 3` — and never had an approximate stage. Both store the same number, so both
 * are edited here, and that is what makes "I put it roughly right, now say exactly what it is"
 * one act instead of a delete and a retype.
 *
 * A locus point on a circle or an arc is not offered. Its `t` also runs from 0 to 1, but there
 * it is an angle wearing a fraction's clothes, and a control offering thirds of a circle would
 * be answering a question nobody asked.
 */

type DivisiblePoint = Entity & {
  readonly source: Extract<Entity['source'], { op: 'ratio-point' | 'locus-point' }>;
};

export interface RatioPanelProps {
  readonly store: EditorStore;
}

/** Whether this entity is a point whose position along a *segment* is a number we may set. */
function isDivisible(
  entity: Entity | undefined,
  document: SceneDocument
): entity is DivisiblePoint {
  if (entity === undefined) return false;
  if (entity.source.op === 'ratio-point') return true;
  if (entity.source.op !== 'locus-point') return false;

  /*
   * A named side of a polygon is a segment, whatever the host as a whole is.
   *
   * This is what makes a point on the side of a triangle behave like a point on a drawn
   * segment — which is what a user expects, having drawn a triangle and clicked on one of its
   * sides. `edge` is only ever set when the host is a polygon and only ever names one side, so
   * its presence is the whole test; the host's own op does not come into it.
   */
  if (entity.source.edge !== undefined) return true;

  return document.entities.get(entity.source.host)?.source.op === 'segment';
}

/**
 * The fractions worth one click.
 *
 * Taken from the exercise book rather than invented: after the midpoint, thirds and quarters
 * are what the problems ask for, and a preset is faster and less error-prone than typing two
 * numbers. Anything else goes in the boxes below.
 */
const COMMON = [
  { of: 1, parts: 3 },
  { of: 1, parts: 2 },
  { of: 2, parts: 3 },
  { of: 1, parts: 4 },
  { of: 3, parts: 4 },
] as const;

export function RatioPanel({ store }: RatioPanelProps) {
  const document = useStore(store, (state) => state.document);
  const selection = useStore(store, (state) => state.selection);
  const commands = useStore(store, (state) => state.commands);

  const points = [...selection]
    .map((id) => document.entities.get(id))
    .filter((entity): entity is DivisiblePoint => isDivisible(entity, document));

  const first = points[0];
  if (first === undefined) return null;

  const ids = points.map((entity) => entity.id);
  const shared = points.every((entity) => entity.source.t === first.source.t)
    ? first.source.t
    : undefined;

  /*
   * Keyed on the selection, so React discards the half-typed fraction when the user picks a
   * different point — the framework's own way of saying "this state belongs to that thing".
   * Clearing it from an effect would work and would also re-render twice and fight the lint
   * rule that exists to stop exactly that.
   */
  return (
    <RatioFields
      key={ids.join(',')}
      count={points.length}
      shared={shared}
      onSet={(t) => {
        commands.setRatio(ids, t);
      }}
    />
  );
}

interface RatioFieldsProps {
  readonly count: number;
  readonly shared: number | undefined;
  readonly onSet: (t: number) => void;
}

function RatioFields({ count, shared, onSet }: RatioFieldsProps) {
  /*
   * The boxes hold text, not numbers, so that a half-typed fraction is allowed to exist.
   * Binding them straight to `t` would mean clearing the denominator divides by nothing and
   * the point jumps to infinity between two keystrokes.
   */
  const [of, setOf] = useState('');
  const [parts, setParts] = useState('');

  const apply = (numerator: number, denominator: number): void => {
    if (denominator === 0 || !Number.isFinite(numerator / denominator)) return;
    onSet(numerator / denominator);
  };

  const typed = { of: Number(of), parts: Number(parts) };
  const typedIsUsable =
    of.trim() !== '' && parts.trim() !== '' && Number.isFinite(typed.of) && typed.parts !== 0;

  return (
    <PanelSection title="Vị trí trên đoạn" {...(count === 1 ? {} : { badge: String(count) })}>
      <div style={{ color: colour.muted, font: '11px/1.4 var(--es-font)' }}>
        {shared === undefined
          ? 'Các điểm đang ở vị trí khác nhau'
          : `Hiện ở ${(shared * 100).toFixed(1)}% chiều dài, tính từ đầu thứ nhất`}
      </div>

      <div style={{ display: 'flex', gap: 2 }}>
        {COMMON.map((option) => {
          const value = option.of / option.parts;
          const active = shared !== undefined && Math.abs(shared - value) < 1e-9;
          return (
            <button
              key={`${String(option.of)}/${String(option.parts)}`}
              type="button"
              title={`AM = ${String(option.of)}/${String(option.parts)} AB`}
              onClick={() => {
                apply(option.of, option.parts);
              }}
              className={active ? 'es-btn es-btn--on' : 'es-btn'}
              aria-pressed={active}
              style={{ flex: 1, padding: '4px 2px', fontSize: 11 }}
            >
              {option.of}/{option.parts}
            </button>
          );
        })}
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ color: colour.muted, fontSize: 11 }}>Tỉ số khác</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            aria-label="Tử số"
            value={of}
            inputMode="numeric"
            onChange={(event) => {
              setOf(event.target.value);
            }}
            style={boxStyle}
          />
          <span style={{ color: colour.muted }}>/</span>
          <input
            aria-label="Mẫu số"
            value={parts}
            inputMode="numeric"
            onChange={(event) => {
              setParts(event.target.value);
            }}
            style={boxStyle}
          />
          <button
            type="button"
            disabled={!typedIsUsable}
            onClick={() => {
              apply(typed.of, typed.parts);
            }}
            className="es-btn es-btn--primary"
            style={{ padding: '4px 10px', fontSize: 11 }}
          >
            Đặt
          </button>
        </div>
      </label>

      {/*
        A fraction over one is not a mistake. "Trên tia đối của tia AB lấy M sao cho..." is a
        real sentence in the book, and it lands past the far end — so the panel says so rather
        than letting a user think they mistyped.
      */}
      <div style={{ color: colour.faint, font: '10px/1.4 var(--es-font)' }}>
        Quá 1 thì điểm ra ngoài đoạn, trên phần kéo dài.
      </div>
    </PanelSection>
  );
}

const boxStyle = {
  width: 48,
  padding: '4px 6px',
  borderRadius: 4,
  border: '1px solid rgba(0,0,0,0.12)',
  font: '11px var(--es-font)',
} as const;
